import { withAuthenticationRequired } from '@auth0/auth0-react'
import { AccessRole, canAccess, Feature } from '@pubstack/common/src/rolesAndFeatures'
import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { ComponentType } from 'react'
import User from '~/auth/User'
import { Forbidden } from '~/components/error/Forbidden'
import { nodeStaffState } from '~/hooks/useNodeStaffAccess'
import { ScopeWithFeatures, useScope } from '~/hooks/useScope'
import { UpgradePage } from '~/modules/monetization/upgrade/UpgradePage'
import { Redirect } from '~/utils/router'
import { useUser } from './user.hooks'

export interface PubstackRouteMeta<T = never> {
  feature?: Feature | Feature[]
  allowDemoRole: boolean
  accessRole: AccessRole
  /**
   * Set when a missing `feature` means "not sold yet" rather than "not allowed": the route stays in the
   * menu and opens the upgrade page instead of Forbidden. Read by both ProtectedRoute and
   * canAccessRoute, so a route is cross-sold on the page and in the sidebar from one declaration.
   *
   * A list, because most of the inventory pages are unlocked by *either* product — a floor rule is worth
   * having whichever way the auction runs. A single offer is still written as one, since naming the one
   * product that is missing is a better pitch than naming both.
   */
  upsell?: UpgradeOffer | UpgradeOffer[]
  dashboardId?: string
  breadcrumbLabel?: React.ReactNode | string | ((params: { search: URLSearchParams; additionalParams?: T }) => string)
  breadcrumbSubtitle?: React.ReactNode | string | ((params: { search: URLSearchParams; additionalParams?: T; user?: User; hasNode?: boolean }) => string | React.ReactNode)
}

/** The offers a route cross-sells, as a list — so an unset `upsell` and an empty one cannot differ. */
export const upsellOffers = (metaData: PubstackRouteMeta): UpgradeOffer[] => (metaData.upsell === undefined ? [] : Array.isArray(metaData.upsell) ? metaData.upsell : [metaData.upsell])

/**
 * `user.hasFeature` synthesises every feature for staff, which would let an admin onto Node by URL on a scope
 * that never had it. Node therefore asks the scope's two toggles, like the menu does (`useCanAccessRoute`):
 * access on, or an admin on a scope onboarded on Node. Not onboarded is off for everybody. Every other
 * feature keeps the viewer's own answer, so staff still reach everything else.
 */
const hasAnyFeature = (user: User, scope: ScopeWithFeatures, feature: Feature | Feature[]): boolean => {
  const features = Array.isArray(feature) ? feature : [feature]
  return features.some((f) => (f === 'pbsStandalone' ? nodeStaffState(scope, user).hasNode : user.hasFeature(f)))
}

// withAuthenticationRequired creates a component: do it ONCE per `component`
// (outside render) to avoid remounting the subtree on every render.
const wrappedComponentCache = new WeakMap<ComponentType, ComponentType>()
const getAuthenticatedComponent = (component: ComponentType): ComponentType => {
  const cached = wrappedComponentCache.get(component)
  if (cached) return cached
  const wrapped = withAuthenticationRequired(component, {
    loginOptions: {
      appState: {
        target: window.location.pathname + window.location.search,
      },
    },
  })
  wrappedComponentCache.set(component, wrapped)
  return wrapped
}

const ProtectedRoute = ({ component, metaData, ...props }: { component: ComponentType; metaData: PubstackRouteMeta }) => {
  const user = useUser()
  const scope = useScope()
  const WrappedComponent = getAuthenticatedComponent(component)

  if (user) {
    if (!metaData.allowDemoRole && user.getScopeRole() === 'demo') {
      return <Forbidden />
    }
    if (!canAccess(user.currentScopeId, user.userRoles, metaData.accessRole)) {
      return <Forbidden />
    }
    if (metaData.feature) {
      if (metaData.feature === 'analytics' && !user.hasFeature('analytics')) {
        if (user.hasFeature('refresh')) return <Redirect to="/analytics/refresh" />
      }

      if (!hasAnyFeature(user, scope, metaData.feature)) {
        // Reached only after the demo and role checks above, so a demo account or a user on an
        // owner-only route still gets Forbidden and never a sales page. Do not reorder them.
        const offers = upsellOffers(metaData)
        return offers.length > 0 ? <UpgradePage offers={offers} /> : <Forbidden />
      }
    }
  }

  // WrappedComponent is derived from a prop (Auth0 HOC required to protect the
  // route), so the rule sees it as non-static. Identity is stabilized by the
  // cache above (no remount); the warning is a residual false positive.
  // oxlint-disable-next-line react/static-components
  return <WrappedComponent {...props} />
}

export default ProtectedRoute
